import { useCallback, useRef, useState } from "react";

import { useJitsu } from "@jitsu/react";
import { toast } from "react-hot-toast";

import { UploadedMedia } from "@/client/media-upload";
import {
  CapturedMedia,
  RecordingDiagnostics,
} from "@/components/webcam-capture/use-webcam-capture";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import {
  captureDegradedRecordingWarning,
  captureRecordingError,
  captureSilentRecordingWarning,
  recordingBreadcrumb,
} from "@/features/socratic/recording-diagnostics";
import {
  selectIsUploading,
  setIsUploading,
} from "@/features/socratic/socratic-slice";
import { describeUploadFailure } from "@/features/socratic/upload-failure";
import {
  CompleteSocraticTurnError,
  useCompleteSocraticTurnMutation,
} from "@/generated/graphql";
import { useMultipartUpload } from "@/hooks/use-multipart-upload";

function reportSubmittedResponse(
  diagnostics: RecordingDiagnostics,
  {
    dialogueId,
    durationSeconds,
    nudgeShown,
  }: { dialogueId: string; durationSeconds: number; nudgeShown: boolean }
): void {
  captureSilentRecordingWarning({
    phase: "response",
    dialogueId,
    durationSeconds,
    diagnostics,
    nudgeShown,
  });

  captureDegradedRecordingWarning({
    dialogueId,
    durationSeconds,
    diagnostics,
  });
}

interface Props {
  workId: string;
  turnId: string | undefined;
  dialogueId: string;
  onCapture?: () => void;
  /**
   * Whether the student was shown the "we can't hear you" nudge at any point.
   * Read at submit time rather than closed over: the nudge may have cleared long
   * before, and a nudge followed by audible audio is a student who recovered.
   */
  wasNudgeShown?: () => boolean;
  /**
   * The moment this client saw recording begin. Read at submit time for the
   * same reason as `wasNudgeShown`, and sent so the turn's integrity record is
   * filled even when the start mutation never reached the server.
   */
  getRecordingStartedAt?: () => string | undefined;
}

/**
 * Hook that handles uploading a recorded Socratic video response and
 * completing the turn via the GraphQL mutation.
 *
 * The upload is resumable (see {@link useMultipartUpload}): a network drop does
 * not discard the recording. On failure the hook returns a non-null
 * `retryUpload`, which the UI surfaces as a "Retry upload" button — the student
 * reconnects (the `ConnectionBar` tells them when), then retries the same
 * recording without re-recording. A retry resumes the multipart upload
 * (re-signing only the parts that did not land); if the upload already finished
 * and only the mutation failed, it skips straight to the mutation.
 */
export function useCompleteSocraticTurn(
  props: Props
): [
  completeTurn: (
    media: CapturedMedia,
    durationSeconds: number,
    diagnostics: RecordingDiagnostics | undefined,
    recordingCompletedAt: string
  ) => Promise<void>,
  isUploading: boolean,
  retryUpload: (() => void) | null,
] {
  const {
    workId,
    turnId,
    dialogueId,
    onCapture,
    wasNudgeShown,
    getRecordingStartedAt,
  } = props;
  const { track } = useJitsu();
  const dispatch = useAppDispatch();
  const isUploading = useAppSelector(selectIsUploading);
  const [completeTurnMutation] = useCompleteSocraticTurnMutation();
  const [uploadFailed, setUploadFailed] = useState(false);

  const upload = useMultipartUpload();

  const lastCaptureRef = useRef<{
    media: CapturedMedia;
    durationSeconds: number;
    diagnostics?: RecordingDiagnostics;
    // Kept so a retry re-sends the moment recording actually stopped. The
    // mutation runs after the upload and a retry can be minutes later, and the
    // server stores whatever it is given.
    recordingCompletedAt: string;
  } | null>(null);
  // Media from a finished upload, kept so a retry after a mutation-only failure
  // does not re-upload the file.
  const uploadedRef = useRef<UploadedMedia | null>(null);

  const runTurn = useCallback(
    async (
      media: CapturedMedia,
      durationSeconds: number,
      diagnostics: RecordingDiagnostics | undefined,
      recordingCompletedAt: string,
      { isRetry }: { isRetry: boolean }
    ) => {
      if (turnId == null) return;
      recordingBreadcrumb("upload attempt", {
        dialogue_id: dialogueId,
        is_retry: isRetry,
        online: navigator.onLine,
      });
      dispatch(setIsUploading(true));
      setUploadFailed(false);
      try {
        const fileData =
          uploadedRef.current ??
          (isRetry ? await upload.resume() : await upload.start(media.file));
        uploadedRef.current = fileData;

        const result = await completeTurnMutation({
          variables: {
            workId,
            input: {
              turnId,
              mediaId: fileData.id,
              mediaUrl: fileData.url,
              durationSeconds,
              recordingStartedAt: getRecordingStartedAt?.(),
              recordingCompletedAt,
            },
          },
        });

        if (result.data?.completeSocraticTurn?.dialogue) {
          uploadedRef.current = null;
          lastCaptureRef.current = null;
          if (diagnostics != null) {
            reportSubmittedResponse(diagnostics, {
              dialogueId,
              durationSeconds,
              nudgeShown: wasNudgeShown?.() ?? false,
            });
          }
          toast.success("Your recording has been completed");
        } else if (result.data?.completeSocraticTurn.error != null) {
          const errorMessage =
            result.data.completeSocraticTurn.error ===
            CompleteSocraticTurnError.ConflictingRecording
              ? "This question has already been answered."
              : "Couldn't save your response.";

          toast.error(
            `${errorMessage} Please return to the main page and try again.`
          );
        }
      } catch (e) {
        // A non-null uploadedRef means the upload landed and only the
        // mutation failed — retry skips straight to the mutation.
        const uploadSucceeded = uploadedRef.current != null;
        const failurePhase = uploadSucceeded
          ? "complete_turn_failed"
          : "upload_failed";
        const failure = describeUploadFailure(e, { uploadSucceeded });

        const context = {
          dialogue_id: dialogueId,
          turn_id: turnId,
          is_retry: isRetry,
          online: navigator.onLine,
          file_size: media.file.size,
          duration_seconds: durationSeconds,
          mime: media.file.type,
          ...failure,
        };

        track(JitsuEvent.VIDEO_RESPONSE_FAILED, {
          error: failurePhase,
          error_message: e instanceof Error ? e.message : String(e),
          ...context,
        });

        captureRecordingError(
          e,
          // tag to help us find the student and their recording
          { work_id: workId, reason: failure.reason },
          context
        );
        toast.error(
          uploadSucceeded
            ? "Sorry, we could not save your response. Check your internet connection and try again"
            : "Sorry, video upload failed. Check your internet connection and try again."
        );
        setUploadFailed(true);
      } finally {
        dispatch(setIsUploading(false));
      }
    },
    [
      turnId,
      workId,
      dialogueId,
      completeTurnMutation,
      track,
      upload,
      dispatch,
      wasNudgeShown,
      getRecordingStartedAt,
    ]
  );

  const onCaptureReady = useCallback(
    async (
      media: CapturedMedia,
      durationSeconds: number,
      diagnostics: RecordingDiagnostics | undefined,
      recordingCompletedAt: string
    ) => {
      lastCaptureRef.current = {
        media,
        durationSeconds,
        diagnostics,
        recordingCompletedAt,
      };
      uploadedRef.current = null;
      onCapture?.();
      await runTurn(media, durationSeconds, diagnostics, recordingCompletedAt, {
        isRetry: false,
      });
    },
    [onCapture, runTurn]
  );

  const retryUpload = useCallback(() => {
    const last = lastCaptureRef.current;
    if (last == null) return;
    runTurn(
      last.media,
      last.durationSeconds,
      last.diagnostics,
      last.recordingCompletedAt,
      { isRetry: true }
    );
  }, [runTurn]);

  return [onCaptureReady, isUploading, uploadFailed ? retryUpload : null];
}
